import { useEffect, useState } from "react";
import { History } from "lucide-react";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { EmptyState, PageHeader, StatusPill } from "@/components/uiext";

/**
 * Audit log viewer. Works for both scopes:
 * - tenant:   GET /api/audit
 * - platform: GET /api/platform/audit
 *
 * Pass `platform` boolean prop to switch to the platform endpoint.
 */
export default function AuditPage({ platform = false }) {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [actionFilter, setActionFilter] = useState("");

  const load = () => {
    const base = platform ? "/platform/audit" : "/audit";
    const qs = actionFilter ? `?action=${encodeURIComponent(actionFilter)}` : "";
    api.get(`${base}${qs}`).then((r) => setItems(r.data));
  };
  useEffect(() => {
    load();
    const tt = setInterval(load, 5000);
    return () => clearInterval(tt);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actionFilter, platform]);

  const kindFor = (action) => {
    if (!action) return "muted";
    if (action.endsWith(".pause") || action.endsWith(".disable")) return "warn";
    if (action.endsWith(".resume") || action.endsWith(".enable")) return "ok";
    if (action.includes("access")) return "security";
    return "info";
  };

  return (
    <div data-testid="page-audit">
      <PageHeader
        title={t("audit.title")}
        subtitle={platform ? t("audit.subtitlePlatform") : t("audit.subtitleTenant")}
        icon={History}
      />
      <Card className="mb-4">
        <CardContent className="p-4 flex flex-wrap items-end gap-3">
          <div className="min-w-[240px]">
            <Label className="text-xs">{t("audit.filterAction")}</Label>
            <Input
              placeholder={t("audit.filterPlaceholder")}
              value={actionFilter}
              onChange={(e) => setActionFilter(e.target.value)}
              data-testid="audit-filter"
            />
          </div>
          <Button variant="outline" onClick={load} data-testid="audit-refresh">
            {t("common.refresh")}
          </Button>
        </CardContent>
      </Card>

      {items.length === 0 ? (
        <EmptyState
          title={t("audit.empty")}
          description={t("audit.emptyDesc")}
        />
      ) : (
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader className="sticky-table-head">
                <TableRow>
                  <TableHead className="w-[200px]">{t("audit.cols.when")}</TableHead>
                  <TableHead>{t("audit.cols.actor")}</TableHead>
                  <TableHead>{t("audit.cols.action")}</TableHead>
                  {platform && <TableHead>{t("audit.cols.org")}</TableHead>}
                  <TableHead>{t("audit.cols.target")}</TableHead>
                  <TableHead>{t("audit.cols.change")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((ev) => (
                  <TableRow key={ev.id} className="row-hover" data-testid={`audit-row-${ev.id}`}>
                    <TableCell className="font-mono text-[11px] text-muted-foreground">
                      {new Date(ev.ts).toLocaleString()}
                    </TableCell>
                    <TableCell>
                      <div className="text-xs font-mono">{ev.actor}</div>
                      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
                        {ev.actor_scope}
                      </div>
                    </TableCell>
                    <TableCell>
                      <StatusPill kind={kindFor(ev.action)}>{ev.action}</StatusPill>
                    </TableCell>
                    {platform && (
                      <TableCell className="text-xs font-mono text-muted-foreground">
                        {ev.org_id || "—"}
                      </TableCell>
                    )}
                    <TableCell className="text-xs">
                      <div className="font-mono">{ev.target_type}</div>
                      <div className="text-[11px] text-muted-foreground font-mono">
                        {ev.target_id || "—"}
                      </div>
                    </TableCell>
                    <TableCell className="text-xs">
                      {ev.before && ev.after ? (
                        <pre className="text-[11px] leading-tight whitespace-pre-wrap break-all">
                          {JSON.stringify(ev.before)} → {JSON.stringify(ev.after)}
                        </pre>
                      ) : ev.detail ? (
                        <pre className="text-[11px] leading-tight whitespace-pre-wrap break-all">
                          {JSON.stringify(ev.detail)}
                        </pre>
                      ) : (
                        "—"
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
